import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Bell, CheckCircle2, DollarSign, MessageSquare, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  PageContainer, PageHeader, StatusPill, Skeleton,
  waitingLabel, relativeTime,
} from "./bdcUi";
import { MyClaimedLeadsWidget, MyEarningsWidget } from "./BdcDashboardWidgets";
import BdcPresenceRail from "./BdcPresenceRail";

const KPI = ({ label, value, tone = "slate", testId, sub, icon: Icon }) => {
  const tones = {
    slate: "bg-white text-slate-900 border-slate-200",
    gold: "bg-[#F7EFD1] text-[#7A5C0E] border-[#F1E1A8]",
    brand: "bg-brand text-white border-brand",
  };
  return (
    <div className={`rounded-2xl px-5 py-4 border ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-semibold uppercase tracking-widest ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{label}</p>
        {Icon && <Icon size={16} className={tone === "brand" ? "text-white/70" : "text-slate-400"} />}
      </div>
      <p className="mt-2 font-display text-2xl sm:text-[28px] font-semibold">{value}</p>
      {sub && <p className={`mt-1 text-[11px] ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
};

const BdcDashboard = () => {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [myChats, setMyChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, q, t] = await Promise.all([
          api.get("/bdc/dashboard"),
          api.get("/bdc/leads", { params: { assignment: "unclaimed", page: 1, page_size: 10, sort: "oldest_waiting" } }),
          api.get("/bdc/threads", { params: { assignment: "mine", is_closed: false } }),
        ]);
        setData(d.data);
        setQueue(q.data.items || []);
        setMyChats(t.data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return (
      <PageContainer>
        <PageHeader title="BDC dashboard" subtitle="Loading queue…" />
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          {[0,1,2,3,4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </PageContainer>
    );
  }

  const g = data.global;
  const me = data.me;

  return (
    <PageContainer>
      <PageHeader
        title="BDC dashboard"
        subtitle={`${g.unclaimed_leads_count} unclaimed · ${g.active_chats_count} active chats · welcome back`}
        testId="bdc-dashboard-header"
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="bdc-kpis">
        <KPI
          label="Unclaimed leads"
          value={g.unclaimed_leads_count}
          tone={g.unclaimed_leads_count > 0 ? "gold" : "slate"}
          icon={Bell}
          testId="bdc-kpi-unclaimed"
        />
        <KPI label="Active chats (mine)" value={me.my_active_leads || g.active_chats_count} icon={MessageSquare} testId="bdc-kpi-active-chats" />
        <KPI label="Appts today" value={g.appts_today_count} icon={Users} testId="bdc-kpi-appts-today" />
        <KPI label="Showed up (mo.)" value={g.showed_up_this_month} sub={`${g.no_show_this_month} no-show`} icon={CheckCircle2} testId="bdc-kpi-showed-up" />
        <KPI label="Revenue (mo.)" value={formatUSD(g.total_revenue_this_month)} sub={`${g.showed_up_this_month} × $300`} tone="brand" icon={DollarSign} testId="bdc-kpi-revenue" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="bdc-queue-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">Live queue</h3>
            <Button asChild variant="ghost" size="sm" className="text-brand"><Link to="/bdc/queue">Open queue →</Link></Button>
          </div>
          {queue.length === 0 ? (
            <p className="text-sm text-slate-500">Queue is empty — nice work! 🎉</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {queue.slice(0, 8).map((lead) => (
                <Link
                  key={lead.id}
                  to={`/bdc/leads/${lead.id}`}
                  className="py-2.5 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 rounded"
                  data-testid={`queue-preview-item-${lead.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{lead.customer?.full_name || lead.guest_name || "Guest"}</p>
                    <p className="text-xs text-slate-500 truncate">{lead.vehicle ? `${lead.vehicle.year} ${lead.vehicle.make} ${lead.vehicle.model}` : "—"} · {lead.dealer?.name}</p>
                  </div>
                  <StatusPill tone="gold">{waitingLabel(lead.created_at)}</StatusPill>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="bdc-mychats">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">My active chats</h3>
            <Button asChild variant="ghost" size="sm" className="text-brand"><Link to="/bdc/chat">Open inbox →</Link></Button>
          </div>
          {myChats.length === 0 ? (
            <p className="text-sm text-slate-500">You haven't claimed a chat yet. Head to the queue to start one.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {myChats.slice(0, 8).map((t) => (
                <Link
                  key={t.id}
                  to={`/bdc/leads/${t.lead_id}`}
                  className="py-2.5 flex items-start gap-3 hover:bg-slate-50 -mx-2 px-2 rounded"
                  data-testid={`bdc-mychats-item-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{t.customer_name || t.guest_email || "Guest"}</p>
                    <p className="text-xs text-slate-500 truncate">{t.last_message_preview || "—"}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{relativeTime(t.last_message_at || t.created_at)}</p>
                  </div>
                  {t.unread_count_bdc > 0 && <StatusPill tone="rose">{t.unread_count_bdc} new</StatusPill>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="bdc-activity-feed">
        <h3 className="font-display text-lg font-semibold text-slate-900">Recent activity</h3>
        {data.recent_activity?.length ? (
          <ul className="mt-3 space-y-2">
            {data.recent_activity.map((ev, i) => (
              <li key={i} className="flex items-start gap-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <Activity size={13} className="mt-1 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{ev.title}</p>
                </div>
                <span className="text-[11px] text-slate-400">{relativeTime(ev.at)}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-500 mt-3">No recent activity.</p>}
      </div>

      {/* Phase 9 dashboard widgets — layout is grid: 2/3 for the work
          widgets, 1/3 for peer-presence rail. Presence rail polls every
          30s; earnings/claimed widgets are one-shot on mount. */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <MyClaimedLeadsWidget />
          <MyEarningsWidget />
        </div>
        <div>
          <BdcPresenceRail />
        </div>
      </div>
    </PageContainer>
  );
};

export default BdcDashboard;
